import { apiRequest } from './client';
import type { AdminRepository } from '../../application/ports/AdminRepository';
import type {
  AdminAlert,
  AdminAlertEvent,
  AdminDevice,
  AdminDeviceMqtt,
  AdminDeviceSensor,
  SensorMapping,
  AdminEmail,
  AdminAiFeature,
  AdminAiTrace,
  AdminAiTraceDetail,
  AdminAiTraceStatus,
  AdminAiUsage,
  AdminAiSettings,
  AdminIrrigationEvent,
  AdminIrrigationSchedule,
  AdminStats,
  AdminUser,
  AdminListParams,
} from '../../domain/Admin';
import type { AdminHub, AdminHubDetail, FirmwareRelease, IssuedClaimCode } from '../../domain/Hub';
import type { AdminLot, AdminLotFormOptions, Lot, LotInput } from '../../domain/Lot';

// La zona horaria del navegador corta los días de los grados día.
const tz = () => encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');

// ?q=&offset=&limit= (y lo que agregue cada lista).
function listQuery(params: AdminListParams, extra: Record<string, string> = {}): string {
  return new URLSearchParams({ q: params.q, offset: String(params.offset), limit: String(params.limit), ...extra }).toString();
}

export class HttpAdminRepository implements AdminRepository {
  fetchStats() {
    return apiRequest<AdminStats>('/admin/stats');
  }

  fetchUsers(params: AdminListParams) {
    return apiRequest<{ users: AdminUser[]; total: number }>(`/admin/users?${listQuery(params)}`);
  }

  updateUser(id: string, input: Partial<{ isAdmin: boolean; isDisabled: boolean }>) {
    return apiRequest<{ user: AdminUser }>(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
  }

  sendPasswordReset(id: string) {
    return apiRequest<void>(`/admin/users/${id}/send-password-reset`, { method: 'POST' });
  }

  fetchDevices(params: AdminListParams) {
    return apiRequest<{ devices: AdminDevice[]; total: number }>(`/admin/devices?${listQuery(params)}`);
  }

  updateDevice(id: string, input: { name: string }) {
    return apiRequest<{ device: AdminDevice }>(`/admin/devices/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
  }

  deleteDevice(id: string) {
    return apiRequest<void>(`/admin/devices/${id}`, { method: 'DELETE' });
  }

  transferDevice(id: string, email: string) {
    return apiRequest<{ device: AdminDevice }>(`/admin/devices/${id}/owner`, { method: 'PUT', body: JSON.stringify({ email }) });
  }

  shareDevice(id: string, email: string, level: 'view' | 'control') {
    return apiRequest<unknown>(`/admin/devices/${id}/shares`, { method: 'POST', body: JSON.stringify({ email, level }) });
  }

  restoreDevice(id: string) {
    return apiRequest<void>(`/admin/devices/${id}/restore`, { method: 'POST' });
  }

  fetchHubs() {
    return apiRequest<{ hubs: AdminHub[] }>('/admin/hubs');
  }

  fetchHub(id: string) {
    return apiRequest<AdminHubDetail>(`/admin/hubs/${id}`);
  }

  assignHub(id: string, email: string) {
    return apiRequest<{ createdStations: string[] }>(`/admin/hubs/${id}/assign`, { method: 'POST', body: JSON.stringify({ email }) });
  }

  fetchFirmware() {
    return apiRequest<{ releases: FirmwareRelease[] }>('/admin/firmware');
  }

  deployFirmware(releaseId: string, hubIds: string[]) {
    return apiRequest<{ results: { hubId: string; status: string; detail: string | null }[] }>(
      `/admin/firmware/${releaseId}/deploy`,
      { method: 'POST', body: JSON.stringify({ hubIds }) }
    );
  }

  setHubBrokerAccess(id: string, enabled: boolean) {
    return apiRequest<unknown>(`/admin/hubs/${id}/broker`, { method: 'POST', body: JSON.stringify({ enabled }) });
  }

  regenerateHubCode(id: string) {
    return apiRequest<IssuedClaimCode>(`/admin/hubs/${id}/claim-code`, { method: 'POST' });
  }

  fetchDeviceMqtt(id: string) {
    return apiRequest<AdminDeviceMqtt>(`/admin/devices/${id}/mqtt`);
  }

  setDeviceTopic(id: string, topic: string) {
    return apiRequest<unknown>(`/admin/devices/${id}/mqtt`, { method: 'PUT', body: JSON.stringify({ topic }) });
  }

  updateSensorMapping(deviceId: string, sensorId: string, mapping: SensorMapping) {
    return apiRequest<{ sensor: AdminDeviceSensor }>(`/admin/devices/${deviceId}/sensors/${sensorId}`, {
      method: 'PUT',
      body: JSON.stringify({ mapping }),
    });
  }

  fetchAlerts(params: AdminListParams & { triggered: boolean }) {
    return apiRequest<{ alerts: AdminAlert[]; total: number }>(`/admin/alerts?${listQuery(params, { triggered: String(params.triggered) })}`);
  }

  updateAlert(id: string, input: { enabled: boolean }) {
    return apiRequest<{ alert: AdminAlert }>(`/admin/alerts/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
  }

  deleteAlert(id: string) {
    return apiRequest<void>(`/admin/alerts/${id}`, { method: 'DELETE' });
  }

  restoreAlert(id: string) {
    return apiRequest<void>(`/admin/alerts/${id}/restore`, { method: 'POST' });
  }

  fetchAlertEvents(params: AdminListParams) {
    return apiRequest<{ events: AdminAlertEvent[]; total: number }>(`/admin/alert-events?${listQuery(params)}`);
  }

  fetchIrrigationSchedules(params: AdminListParams) {
    return apiRequest<{ schedules: AdminIrrigationSchedule[]; total: number }>(`/admin/irrigation-schedules?${listQuery(params)}`);
  }

  fetchIrrigationEvents(params: AdminListParams) {
    return apiRequest<{ events: AdminIrrigationEvent[]; total: number }>(`/admin/irrigation-events?${listQuery(params)}`);
  }

  fetchAiUsage(days: number) {
    return apiRequest<AdminAiUsage>(`/admin/ai/usage?days=${days}`);
  }

  fetchAiTraces(params: AdminListParams & { feature: AdminAiFeature | null; status: AdminAiTraceStatus | null }) {
    const extra: Record<string, string> = {};
    if (params.feature) extra.feature = params.feature;
    if (params.status) extra.status = params.status;
    return apiRequest<{ traces: AdminAiTrace[]; total: number }>(`/admin/ai/traces?${listQuery(params, extra)}`);
  }

  fetchAiTrace(id: string) {
    return apiRequest<{ trace: AdminAiTraceDetail }>(`/admin/ai/traces/${encodeURIComponent(id)}`);
  }

  fetchAiSettings() {
    return apiRequest<{ settings: AdminAiSettings }>('/admin/ai/settings');
  }

  updateAiSettings(input: Partial<AdminAiSettings>) {
    return apiRequest<{ settings: AdminAiSettings }>('/admin/ai/settings', { method: 'PUT', body: JSON.stringify(input) });
  }

  fetchEmails(params: AdminListParams & { type: string | null }) {
    return apiRequest<{ emails: AdminEmail[]; total: number }>(`/admin/emails?${listQuery(params, params.type ? { type: params.type } : {})}`);
  }

  fetchLots(params: AdminListParams) {
    return apiRequest<{ lots: AdminLot[]; total: number }>(`/admin/lots?${listQuery(params)}&tz=${tz()}`);
  }

  fetchLot(id: string) {
    return apiRequest<{ lot: Lot }>(`/admin/lots/${id}?tz=${tz()}`);
  }

  fetchLotFormOptions(id: string) {
    return apiRequest<AdminLotFormOptions>(`/admin/lots/${id}/form-options`);
  }

  updateLot(id: string, input: Partial<LotInput>) {
    return apiRequest<{ lot: Lot }>(`/admin/lots/${id}?tz=${tz()}`, { method: 'PATCH', body: JSON.stringify(input) });
  }

  observeLotStage(id: string, stageIndex: number) {
    return apiRequest<{ lot: Lot }>(`/admin/lots/${id}/observed-stage?tz=${tz()}`, { method: 'POST', body: JSON.stringify({ stageIndex }) });
  }
}
